import React from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import classnames from 'classnames';
import { Form, DatePicker, TimePicker } from 'antd';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { isDateBeforeCurrentDate, isDateAfterCurrentDate, getDateValue } from '../../../common/dateUtils';
import FundkyTooltip from '../FundkyTooltip';

import './DateAndTimePickerField.less';

class DateAndTimePickerField extends React.Component {

  onChange = (value, field) => {
    const
      { form, fieldId, dateFormat, timeFormat } = this.props,
      { getFieldValue, setFieldsValue } = form;

    let date, time, dateTime;

    // Date field changed
    if (field === 'date') {
      // Extract and format from field
      date = value && value.format(dateFormat) || null;
      // Set full date without time
      dateTime = date ? date + " 00:00:00" : null;


      // Get Time field value
      if (getFieldValue(fieldId + '_time')) {
        // Extract and format from field
        time = getFieldValue(fieldId + '_time').format(timeFormat);
        // Set full date
        dateTime = date ? date + " " + time : moment().format(dateFormat) + " " + time;
      }
    } else
    // Time field changed
    if (field === 'time') {
      // Extract and format from field
      time = value && value.format(timeFormat) || null;
      // Set full date without date
      dateTime = time ? moment().format(dateFormat) + " " + time : null;

      // Get Date field value
      if (getFieldValue(fieldId + '_date')) {
        // Extract and format from field
        date = getFieldValue(fieldId + '_date').format(dateFormat);
        // Set full date
        dateTime = time ? date + " " + time : date + " 00:00:00";
      }
    }

    // Push result to hidden field
    setFieldsValue({ [fieldId]: dateTime ? moment(dateTime) : null });
    // Push result to parent onChange function
    this.props.onChange(dateTime ? moment(dateTime) : null)
  }

  render() {
    const {
      t,
      locale,
      form,
      fieldId,
      className,
      initialValue,
      fieldOptions,
      disabled,
      showTime,
      dateFormat,
      timeFormat,
      label,
      placeholder,
      colon,
      hasFeedback,
      required,
      requiredMessage,
      notice,
      tooltip,
      past,
      future,
      present,
      allowClear,
      mode
    } = this.props,
    { getFieldDecorator } = form;

    const inputDate = (
      <DatePicker
        disabled={disabled}
        showTime={showTime}
        format={dateFormat}
        placeholder={placeholder}
        allowClear={allowClear}
        mode={mode}
        suffixIcon={<FontAwesomeIcon icon={['fal', 'calendar-alt']} />}
      />
    );
    const inputTime = (
      <TimePicker
        disabled={disabled}
        format={timeFormat}
        placeholder={placeholder}
        allowClear={allowClear}
        suffixIcon={<FontAwesomeIcon icon={['fal', 'clock']} />}
      />
    );

    const requiredValidator = required
      ? [
          {
            type: 'object',
            required: required,
            message: requiredMessage || t('FIELDS-VALIDATION:required-message')
          }
        ]
      : [];

    // TODO: Update the pastValidator to 2 fields context
    const pastValidator = past
      ? [
          {
            validator: (rule, value, callback) => {
              return value === null ||
                past === true && isDateBeforeCurrentDate(value.format('YYYY-MM-DD HH:mm:ss'), true) ||
                typeof past === 'object' && past.diff(value) > 0 ||
                typeof past === 'string' && moment(past).diff(value) > 0
                  ? callback()
                  : callback(true);
            },
            message: t('FIELDS-VALIDATION:date.past', {
              time: past === true
                ? t('FIELDS-VALIDATION:date.now')
                : past.format('YYYY-MM-DD HH:mm:ss')
            })
          }
        ]
      : [];

    // TODO: Update the futureValidator to 2 fields context
    const futureValidator = future
      ? [
          {
            validator: (rule, value, callback) => {
              return value === null ||
                future === true && isDateAfterCurrentDate(value.format('YYYY-MM-DD HH:mm:ss'), true) ||
                typeof future === 'object' && future.diff(value) < 0 ||
                typeof future === 'string' && monent(future).diff(value) < 0
                  ? callback()
                  : callback(true);
            },
            message: t('FIELDS-VALIDATION:date.future', {
              time: future === true
                ? t('FIELDS-VALIDATION:date.now')
                : future.format('YYYY-MM-DD HH:mm:ss')
            })
          }
        ]
      : [];

    const rules = {
      rules: [...requiredValidator, ...pastValidator, ...futureValidator, ...this.props.rules]
    };

    const dateOptions = {
      initialValue: initialValue && moment(initialValue) || null,
      ...rules,
      ...fieldOptions,
      onChange: value => this.onChange(value, 'date')
    };

    const timeOptions = {
      initialValue: initialValue && moment(initialValue) || null,
      ...rules,
      ...fieldOptions,
      onChange: value => this.onChange(value, 'time')
    };

    return (
      <>
        <Form.Item
          className={classnames('DateAndTimePickerField', className)}
          hasFeedback={hasFeedback}
          label={
            label && (
              <span>
                {label}
                {tooltip && ' :'}
                {tooltip && <FundkyTooltip title={tooltip} />}
              </span>
            )
          }
          colon={tooltip ? false : true}
        >
          {getFieldDecorator(fieldId + '_date', dateOptions)(inputDate)}
          {getFieldDecorator(fieldId + '_time', timeOptions)(inputTime)}
          {getFieldDecorator(fieldId, { initialValue: initialValue && moment(initialValue) || null })(<input type="hidden" />)}
        </Form.Item>
        {notice && <span className="notice">{notice}</span>}
      </>
    );
  }
}

DateAndTimePickerField.defaultProps = {
  fieldId: 'datePicker',
  fieldOptions: {},
  placeholder: '',
  disabled: false,
  initialValue: null,
  hasFeedback: false,
  required: false,
  requiredMessage: null,
  dateFormat: 'YYYY-MM-DD',
  timeFormat: 'HH:mm:ss',
  label: false,
  colon: false,
  showTime: false,
  notice: null,
  allowClear: false,
  tooltip: null,
  past: false,
  future: false,
  present: false,
  mode: null,
  rueles: [],
  onChange: () => {}
};

DateAndTimePickerField.propTypes = {
  form: PropTypes.object.isRequired,
  fieldId: PropTypes.string.isRequired,
  required: PropTypes.bool,
  fieldOptions: PropTypes.object,
  className: PropTypes.string,
  hasFeedback: PropTypes.bool,
  disabled: PropTypes.bool,
  colon: PropTypes.bool,
  showTime: PropTypes.bool,
  mode: PropTypes.oneOf([null, 'time', 'date', 'month', 'year', 'decade']),
  rules: PropTypes.array,
  onChange: PropTypes.func
};

export default translate('DateAndTimePickerField')(DateAndTimePickerField);
